import React, { memo, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import CarrierStatus from 'components/marketplace/CarrierStatus'
import { Button } from 'transfix-ui/components/Button'
import { undeleteUser, setUndeleteUserId } from 'utils/actions/user'
import { IChildUser } from 'apps/user/types/user'
import {
  selectUndeleteError,
  selectUndeletedSuccess,
  selectUndeleteIsLoading,
  selectUndeletedId,
} from 'apps/user/utils/selectors/userProfile'
import { Dropdown, DropdownIconType } from 'components/forms'
import { Toast } from 'transfix-ui'

interface IUserAccountRowProps {
  user: IChildUser
  canBeDeleted: boolean
  editUser: (userId: string) => void
  deleteUser: (userId: string) => void
}

export const UserAccountRow = memo<IUserAccountRowProps>(
  ({ canBeDeleted, deleteUser, editUser, user }) => {
    const dispatch = useDispatch()
    const undeleteId = useSelector(selectUndeletedId)
    const undeleteSuccess = useSelector(selectUndeletedSuccess)
    const undeleteLoading = useSelector(selectUndeleteIsLoading)
    const undeleteError = useSelector(selectUndeleteError)
    const [showMessage, setShowMessage] = useState(false)
    const [isLoadingUndelete, setIsLoadingUndelete] = useState(false)
    const [isUndeletedUser, setIsUndeletedUser] = useState(false)

    const {
      companyApplicationState: applicationState,
      companyDotNumber: dotNumber,
      companyId,
      companyMcNumber: mcNumber,
      companyName: accountName,
      deletedAt,
      id,
      roleName,
      source,
      stateName,
      userTypeName,
    } = user

    useEffect(() => {
      if (id === undeleteId) {
        setIsUndeletedUser(true)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [undeleteId])

    useEffect(() => {
      if (isUndeletedUser) {
        setShowMessage(Boolean(undeleteError) || Boolean(undeleteSuccess))
      }
    }, [dispatch, isUndeletedUser, undeleteError, undeleteSuccess])

    useEffect(() => {
      if (isUndeletedUser) {
        setIsLoadingUndelete(undeleteLoading)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [undeleteLoading])

    const toastMessage = undeleteSuccess ? 'User is undeleted' : undeleteError
    const toastStyle = undeleteSuccess ? 'default' : 'alert'

    const isSourceShipperApp = source === 'shipper'
    const showDropdown = !(deletedAt || isSourceShipperApp)

    const editUserAccountHandler = () => editUser(id)
    const deleteUserAccountHandler = () => deleteUser(id)
    const undeleteUserAccountHandler = () => {
      dispatch(setUndeleteUserId(id))
      dispatch(undeleteUser(id))
    }
    const closeMessageHandler = () => setShowMessage(false)

    return (
      <li className='flex items-center justify-between pv4'>
        <div>
          <p className='pb0 flex'>
            <strong>
              <a className='mr2' href={`${__ROOT__}/carriers/${companyId}`}>
                {accountName}
              </a>
            </strong>
            <CarrierStatus status={applicationState} showTooltip />
          </p>
          {(mcNumber || dotNumber) && (
            <p className='pb0'>
              {mcNumber && <span>MC: {mcNumber}</span>}
              {mcNumber && dotNumber && <span> &middot; </span>}
              {dotNumber && <span>DOT: {dotNumber} </span>}
            </p>
          )}
          <p>
            <span>
              <strong>{userTypeName}</strong>&nbsp;&middot;&nbsp;
            </span>
            <span>{roleName}</span>
            <span className='text-secondary' data-test='user-state'>
              &nbsp;{stateName}
            </span>
          </p>
          {isSourceShipperApp && (
            <p>
              <span className='mr1 text-secondary'>Created by:</span>
              Pro Shipper in Shipper App
            </p>
          )}
        </div>
        <Toast
          open={showMessage}
          onClose={closeMessageHandler}
          variation={toastStyle}
          message={toastMessage}
        />
        {deletedAt && (
          <Button
            onClick={undeleteUserAccountHandler}
            disabled={isLoadingUndelete}
            data-test='undelete-user'>
            {isLoadingUndelete ? 'Undeleting ...' : 'Undelete'}
          </Button>
        )}
        {showDropdown && (
          <Dropdown
            options={
              canBeDeleted
                ? [
                    {
                      label: 'Edit Account',
                      action: editUserAccountHandler,
                    },
                    {
                      label: 'Delete Account',
                      action: deleteUserAccountHandler,
                    },
                  ]
                : [
                    {
                      label: 'Edit Account',
                      action: editUserAccountHandler,
                    },
                  ]
            }
            dataTest={`user-account-${companyId}-context-menu`}
            dropdownId={`user-account-${companyId}-context-menu`}
            icon={DropdownIconType.ellipsis}
          />
        )}
      </li>
    )
  }
)
